import * as React from 'react';
import {
  Autocomplete,
  AutocompleteOption,
  IconButton,
  Typography,
} from '@mui/joy';
import { XIcon } from 'lucide-react';
import {
  getModelHistoryKey,
  readModelHistory,
  removeModelFromHistory,
} from './modelHistory';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ModelNameInputProps {
  /** Current value (controlled). */
  value: string;
  /** Called whenever the input value changes (free-form typing or selection). */
  onChange: (value: string) => void;
  /**
   * The interactive_type used to scope the history.
   * e.g. 'vllm', 'ollama', 'mlx'. Pass `undefined` to use the
   * default (shared) bucket.
   */
  taskTypeOrId?: string | null;
  /** Placeholder text shown when the field is empty. */
  placeholder?: string;
  /** Whether the input is disabled. */
  disabled?: boolean;
  /** Whether the field is required. */
  required?: boolean;
}

/**
 * A freeSolo Joy UI Autocomplete that shows previously typed model names
 * as dropdown suggestions, scoped by task type.
 *
 * Call `saveModelToHistory(getModelHistoryKey(taskTypeOrId), value)` from
 * the parent's submit handler to persist the entered value.
 */
export default function ModelNameInput({
  value,
  onChange,
  taskTypeOrId,
  placeholder = 'e.g. meta-llama/Llama-3-8B',
  disabled = false,
  required = false,
}: ModelNameInputProps) {
  const storageKey = getModelHistoryKey(taskTypeOrId);

  // Re-read history whenever the modal opens (taskTypeOrId changes) or after
  // the user removes an entry.
  const [history, setHistory] = React.useState<string[]>(() =>
    readModelHistory(storageKey),
  );

  // Refresh the history list when the task type changes (e.g. user switched
  // template in the same modal session).
  React.useEffect(() => {
    setHistory(readModelHistory(storageKey));
  }, [storageKey]);

  const handleRemoveEntry = React.useCallback(
    (entry: string, e: React.MouseEvent) => {
      e.preventDefault();
      removeModelFromHistory(storageKey, entry);
      setHistory(readModelHistory(storageKey));
    },
    [storageKey],
  );

  return (
    <Autocomplete
      freeSolo
      // Provide the saved history as options
      options={history}
      // Control both the input value and the autocomplete value
      value={value || null}
      inputValue={value}
      // Only handle free-form typing via onInputChange
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === 'input') {
          onChange(newInputValue ?? '');
        }
      }}
      // Handle clearing via the clear button (value becomes null)
      onChange={(_event, newValue) => {
        if (newValue === null) {
          onChange('');
        } else if (typeof newValue === 'string') {
          onChange(newValue);
        }
      }}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      // Render each option with an inline ✕ button to remove it from history
      renderOption={(props, option) => (
        <AutocompleteOption
          key={option}
          {...props}
          sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
        >
          <Typography
            level="body-sm"
            sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {option}
          </Typography>
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            aria-label={`Remove ${option} from history`}
            onMouseDown={(e) => {
              e.preventDefault();
              handleRemoveEntry(option, e as any);
            }}
            sx={{ flexShrink: 0, minWidth: 'unset', p: 0.25 }}
          >
            <XIcon size={12} />
          </IconButton>
        </AutocompleteOption>
      )}
      // Keep the same visual style as the existing Joy UI <Input> fields
      sx={{ width: '100%' }}
    />
  );
}
